import { Middleware } from 'redux';
import thunkMiddleware from 'redux-thunk';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { configureStore } from '@reduxjs/toolkit';
import rootReducer from './reducers';
import { ClientIdAndUserIdReturnType, getAuthUserId } from '../hooks/useAuthUserId';
import sentryLogger from '../services/sentry';
import { STATE_NAMES } from './slices/StateNames';

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['warningBar'],
};

const addUserIdToActionMiddleware: Middleware<{}, RootState> = (store) => (next) => (action) => {
  const state = store.getState();
  const userIdAndClientId: ClientIdAndUserIdReturnType = getAuthUserId(state);

  // Adding userIdAndClientId to the action payload
  const newAction = {
    ...action,
    ...userIdAndClientId,
  };

  // Pass the modified action to the next middleware or Redux store
  return next(newAction);
};

const localStorageMiddleware: Middleware<{}, RootState> = (store) => (next) => (action) => {
  const result = next(action);

  try {
    const state = store.getState();

    const persistState = {
      [STATE_NAMES.ACTIVE_PROJECTS_PAGE]: state[STATE_NAMES.ACTIVE_PROJECTS_PAGE],
      [STATE_NAMES.COMPLETED_PROJECTS_PAGE]: state[STATE_NAMES.COMPLETED_PROJECTS_PAGE],
      [STATE_NAMES.CONCEPT_BRIEFINGS_PAGE]: state[STATE_NAMES.CONCEPT_BRIEFINGS_PAGE],
    };

    localStorage.setItem("reduxState", JSON.stringify(persistState));
  } catch (error) {
    sentryLogger.exception(error);
  }

  return result;
};

export const loadState = () => {
  try {
    const serializedState = localStorage.getItem("reduxState");
    return serializedState ? JSON.parse(serializedState) : undefined;
  } catch (error) {
    sentryLogger.exception(error);
    return undefined;
  }
};

const preloadedState = loadState();

const persistedReducer = persistReducer(persistConfig, rootReducer);

function createStore() {
  return configureStore({
    reducer: persistedReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().prepend(thunkMiddleware, addUserIdToActionMiddleware, localStorageMiddleware),
  });
}

const store = createStore();
export const persistor = persistStore(store);

export default store;

export type RootState = ReturnType<typeof persistedReducer>;
export type AppDispatch = typeof store.dispatch;
